import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Material, MaterialCategory, createMaterial, updateMaterial, getMaterialCategories, createMaterialCategory } from '../lib/materials';

const UNIT_OPTIONS = [
    { value: 'foot', label: 'Foot (ft)', group: 'Length' },
    { value: 'meter', label: 'Meter (m)', group: 'Length' },
    { value: 'square_foot', label: 'Square Foot (sq ft)', group: 'Area' },
    { value: 'square_meter', label: 'Square Meter (sq m)', group: 'Area' },
    { value: 'gallon', label: 'Gallon (gal)', group: 'Volume' },
    { value: 'half_gallon', label: 'Half Gallon (½ gal)', group: 'Volume' },
    { value: 'quart', label: 'Quart (qt)', group: 'Volume' },
    { value: 'liter', label: 'Liter (L)', group: 'Volume' }
];

interface CostCalculator {
  total_cost: string;
  length?: string;
  width?: string;
  volume?: string;
}

interface MaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  item?: Material | null;
  mode: 'create' | 'edit';
}

export default function MaterialModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  item,
  mode 
}: MaterialModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [useCostCalculator, setUseCostCalculator] = useState(false);
  const [calculator, setCalculator] = useState<CostCalculator>({
    total_cost: '',
    length: '',
    width: '',
    volume: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cost: '',
    unit: UNIT_OPTIONS[0].value,
    category_id: ''
  });

  // Calculate cost based on calculator inputs
  const calculateCost = () => {
    const totalCost = parseFloat(calculator.total_cost) || 0;
    
    // Get unit type from selected unit
    const selectedUnit = UNIT_OPTIONS.find(u => u.value === formData.unit);
    const unitGroup = selectedUnit?.group;
    
    if (!totalCost) return;
    
    let calculatedCost = 0;
    
    switch (unitGroup) {
      case 'Length':
        const length = parseFloat(calculator.length || '0');
        if (length) {
          calculatedCost = totalCost / length;
        }
        break;
        
      case 'Area':
        const areaLength = parseFloat(calculator.length || '0');
        const width = parseFloat(calculator.width || '0');
        if (areaLength && width) {
          const area = areaLength * width;
          calculatedCost = totalCost / area;
        }
        break;
        
      case 'Volume':
        const volume = parseFloat(calculator.volume || '0');
        if (volume) {
          calculatedCost = totalCost / volume;
        }
        break;
    }
    
    if (calculatedCost > 0) {
      setFormData(prev => ({ ...prev, cost: calculatedCost.toFixed(2) }));
    }
  };

  // Update cost when calculator values change
  useEffect(() => {
    if (useCostCalculator) {
      calculateCost();
    }
  }, [calculator, formData.unit]);

  const loadCategories = async () => {
    if (!businessId) return;
    
    try {
      const data = await getMaterialCategories(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      if (mode === 'edit' && item) {
        setFormData({
          name: item.name,
          description: item.description || '',
          cost: item.cost.toString(),
          unit: item.unit,
          category_id: item.category_id || ''
        });
      }
    }
  }, [isOpen, item, mode]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        description: '',
        cost: '',
        unit: UNIT_OPTIONS[0].value,
        category_id: ''
      });
      setError(null);
    }
  };

  const handleAddCategory = async () => {
    if (!businessId || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createMaterialCategory({
        business_id: businessId,
        name: newCategoryName.trim(),
        order_index: categories.length
      });
      
      await loadCategories();
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up category_id - convert empty string to null
      const cleanedFormData = {
        ...formData,
        category_id: formData.category_id || null,
        cost: parseFloat(formData.cost) || 0
      };

      if (mode === 'create') {
        await createMaterial({
          business_id: businessId,
          ...cleanedFormData
        });
      } else if (mode === 'edit' && item) {
        await updateMaterial(item.id, cleanedFormData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving material:', err);
      setError(err instanceof Error ? err.message : 'Failed to save material');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {mode === 'create' ? 'New Material' : 'Edit Material'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Enter material name"
                />
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                {isAddingCategory ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="flex-1 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter category name"
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      disabled={!newCategoryName.trim()}
                      className="px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingCategory(false);
                        setNewCategoryName('');
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                      className="flex-1 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    >
                      <option value="">No Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setIsAddingCategory(true)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      New Category
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Enter material description"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost Method
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={!useCostCalculator}
                        onChange={() => setUseCostCalculator(false)}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="ml-2 text-sm text-gray-700">Direct Cost</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={useCostCalculator}
                        onChange={() => setUseCostCalculator(true)}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="ml-2 text-sm text-gray-700">Calculator</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit
                  </label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  >
                    {UNIT_OPTIONS.map((unit, index) => (
                      <React.Fragment key={unit.value}>
                        {(index === 0 || UNIT_OPTIONS[index - 1].group !== unit.group) && (
                          <optgroup label={unit.group}>
                            <option value={unit.value}>{unit.label}</option>
                          </optgroup>
                        )}
                        {(index > 0 && UNIT_OPTIONS[index - 1].group === unit.group) && (
                          <option value={unit.value}>{unit.label}</option>
                        )}
                      </React.Fragment>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Cost Input Section */}
              <div>
                {useCostCalculator ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Cost
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={calculator.total_cost}
                          onChange={(e) => setCalculator(prev => ({ ...prev, total_cost: e.target.value }))}
                          className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Length/Area Fields */}
                    {(formData.unit.includes('foot') || formData.unit.includes('meter')) && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Length
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={calculator.length}
                            onChange={(e) => setCalculator(prev => ({ ...prev, length: e.target.value }))}
                            className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                          />
                        </div>
                        
                        {(formData.unit.includes('square')) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Width
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={calculator.width}
                              onChange={(e) => setCalculator(prev => ({ ...prev, width: e.target.value }))}
                              className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Volume Field */}
                    {(formData.unit.includes('gallon') || formData.unit.includes('liter') || formData.unit.includes('quart')) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Volume
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={calculator.volume}
                          onChange={(e) => setCalculator(prev => ({ ...prev, volume: e.target.value }))}
                          className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                      </div>
                    )}
                    
                    {/* Calculated Cost Display */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-900">
                        Calculated Cost: ${formData.cost || '0.00'} per {formData.unit}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Direct Cost
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.cost}
                        onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                        className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Material' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}